import React, { useEffect, useState, useCallback } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Input,
  Label,
  Spinner,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
} from "reactstrap";
import { useSelector } from "react-redux";
import { RootState } from "../../redux-toolkit/store";
import { API } from "../../api/API";
import { getData } from "../../CommonElements/Functions/PostGetFun";
import CardHeaderSpan from "../../Utils/CommonComponents/CardHeaderSpan";
import FeatherIconCom from "../../Utils/CommonComponents/CommonIcons/FeatherIconCom";
import CommonTableNew, {
  HeadDataItem,
} from "../Tables/ReactstrapTables/BasicTables/Common/CommonTableNew";
import TreeModel from "../Dashboards/Attendance/AttendanceTable/TreeModel";
import { getFilteredPositionIds } from "../../db/DexieServises";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import Loader from "../../Layout/Loader";

interface LeaveData {
  LeaveYear: string;
  LeaveType: string;
  LeaveTypeName?: string;
  Accuration: number;
  Opening: number;
  Reward: number;
  Consumed: number;
  Balance: number;
  UniqueCode: string;
}

interface EmployeeLeaveData {
  EmployeeCode: string;
  FirstName: string;
  LastName: string;
  Phone: string;
  CreatedAt: string;
  Position: string;
  Leaves: LeaveData[];
}

interface FlattenedLeaveData {
  EmployeeCode: string;
  FirstName: string;
  LastName: string;
  Phone: string;
  CreatedAt: string;
  Position: string;
  LeaveYear: string;
  LeaveType: string;
  LeaveTypeName: string;
  Accuration: number;
  Opening: number;
  Reward: number;
  Consumed: number;
  Balance: number;
  UniqueCode: string;
}

interface LeaveTypeOption {
  value: string;
  label: string;
}

const headData: HeadDataItem[] = [
  { id: "1", head: "Emp Code", key: "EmployeeCode", sortKey: "EmployeeCode" },
  { id: "2", head: "First Name", key: "FirstName", sortKey: "FirstName" },
  { id: "3", head: "Last Name", key: "LastName", sortKey: "LastName" },
  { id: "4", head: "Phone", key: "Phone", sortKey: "Phone" },
  { id: "5", head: "Created At", key: "CreatedAt", sortKey: "CreatedAt" },
  { id: "6", head: "Position", key: "Position", sortKey: "Position" },
  { id: "7", head: "Leave Year", key: "LeaveYear", sortKey: "LeaveYear" },
  { id: "8", head: "Leave Type", key: "LeaveType", sortKey: "LeaveType" },
  { id: "9", head: "Leave Type Name", key: "LeaveTypeName", sortKey: "LeaveTypeName" },
  { id: "10", head: "Accuration", key: "Accuration", sortKey: "Accuration" },
  { id: "11", head: "Opening", key: "Opening", sortKey: "Opening" },
  { id: "12", head: "Reward", key: "Reward", sortKey: "Reward" },
  { id: "13", head: "Consumed", key: "Consumed", sortKey: "Consumed" },
  { id: "14", head: "Balance", key: "Balance", sortKey: "Balance" },
  { id: "15", head: "Unique Code", key: "UniqueCode", sortKey: "UniqueCode" },
];

const LeaveBalanceReport: React.FC = () => {
  const unitId = useSelector((state: RootState) => state.orgUnit.id);
  const serverUrl = localStorage.getItem("serverUrl");
  const defaultLeaveTypes: LeaveTypeOption[] = [
    { value: "Medical Leave", label: "Medical Leave" },
    { value: "Casual Leave", label: "Casual Leave" },
    { value: "Sick Leave", label: "Sick Leave" },
    { value: "Leave Without Pay", label: "Leave Without Pay" },
    { value: "Maternity Leave", label: "Maternity Leave" },
    { value: "Paternity leave", label: "Paternity leave" },
    { value: "Bereavement Leave", label: "Bereavement Leave" },
    { value: "Privilege leave", label: "Privilege leave" },
    { value: "Election Leave", label: "Election Leave" },
  ];
  const [data, setData] = useState<FlattenedLeaveData[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedIds, setSelectedIds] = useState<(string | number)[]>([]);
  const [leaveTypes, setLeaveTypes] =
    useState<LeaveTypeOption[]>(defaultLeaveTypes);
  const [selectedLeaveTypes, setSelectedLeaveTypes] = useState<string[]>([]);
  const [leaveYear] = useState<string>(new Date().getFullYear().toString());
  const [leaveTypeDropdownOpen, setLeaveTypeDropdownOpen] = useState(false);
  console.log("======= leaveTypes ==========", leaveTypes);
  console.log("======= selectedLeaveTypes ==========", selectedLeaveTypes);

  // Fetch leave types on mount
  useEffect(() => {
    const fetchLeaveTypes = async () => {
      try {
        const url = `${serverUrl}${API.leaveTypes}`;
        const { status, data: responseData } = await getData(url, true);
        if (status === 200 && responseData?.data) {
          console.log(
            "======== responseData?.data =========",
            responseData?.data,
          );
          const types = Object.entries(responseData.data).map(
            ([key, value]: any) => ({
              value: key,
              label: value,
            }),
          );
          setLeaveTypes(types.length > 0 ? types : defaultLeaveTypes);
        }
      } catch (error) {
        console.error("Error fetching leave types:", error);
      }
    };
    fetchLeaveTypes();
  }, [serverUrl]);

  // Fetch initial position IDs
  useEffect(() => {
    const fetchPositionIds = async () => {
      if (unitId) {
        const ids = await getFilteredPositionIds("Position", unitId);
        setSelectedIds(ids);
      }
    };
    fetchPositionIds();
  }, [unitId]);

  const fetchData = useCallback(async () => {
    if (!unitId) return;
    setLoading(true);
    try {
      const positionIds =
        selectedIds.length > 0
          ? selectedIds.join(",")
          : (await getFilteredPositionIds("Position", unitId)).join(",");

      const params: Record<string, string> = {
        org_unit_id: String(unitId),
        position_ids: positionIds,
        leave_year: leaveYear,
      };

      if (selectedLeaveTypes.length > 0) {
        params.leave_types = selectedLeaveTypes.join(",");
      }

      const url = `${serverUrl}${API.leaveBalanceReport}`;
      const { status, data: responseData } = await getData(url, true, params);

      if (status === 200 && responseData?.data) {
        // Flatten the data - each leave record becomes a separate row
        const flattenedData: FlattenedLeaveData[] = [];
        responseData.data.forEach((employee: EmployeeLeaveData) => {
          if (employee.Leaves && employee.Leaves.length > 0) {
            employee.Leaves.forEach((leave: LeaveData) => {
              flattenedData.push({
                EmployeeCode: employee.EmployeeCode,
                FirstName: employee.FirstName,
                LastName: employee.LastName,
                Phone: employee.Phone,
                CreatedAt: employee.CreatedAt,
                Position: employee.Position,
                LeaveYear: leave.LeaveYear,
                LeaveType: leave.LeaveType,
                LeaveTypeName: leave.LeaveTypeName || leave.LeaveType || "",
                Accuration: leave.Accuration,
                Opening: leave.Opening,
                Reward: leave.Reward,
                Consumed: leave.Consumed,
                Balance: leave.Balance,
                UniqueCode: leave.UniqueCode,
              });
            });
          } else {
            // Employee with no leaves
            flattenedData.push({
              EmployeeCode: employee.EmployeeCode,
              FirstName: employee.FirstName,
              LastName: employee.LastName,
              Phone: employee.Phone,
              CreatedAt: employee.CreatedAt,
              Position: employee.Position,
              LeaveYear: "",
              LeaveType: "",
              LeaveTypeName: "",
              Accuration: 0,
              Opening: 0,
              Reward: 0,
              Consumed: 0,
              Balance: 0,
              UniqueCode: "",
            });
          }
        });
        setData(flattenedData);
      }
    } catch (error) {
      console.error("Error fetching leave balance data:", error);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [unitId, selectedIds, selectedLeaveTypes, leaveYear, serverUrl]);

  useEffect(() => {
    if (selectedIds.length > 0) {
      fetchData();
    }
  }, [selectedIds, fetchData]);

  const handleLeaveTypeChange = (type: string) => {
    setSelectedLeaveTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type],
    );
  };

  const toggleLeaveTypeDropdown = () =>
    setLeaveTypeDropdownOpen((prev) => !prev);

  const handleApplyFilters = () => {
    fetchData();
  };

  const filteredData = data.filter((row) => {
    const search = searchTerm.toLowerCase();
    return (
      row.EmployeeCode?.toLowerCase().includes(search) ||
      row.FirstName?.toLowerCase().includes(search) ||
      row.LastName?.toLowerCase().includes(search) ||
      row.Phone?.toLowerCase().includes(search) ||
      row.LeaveTypeName?.toLowerCase().includes(search) ||
      row.LeaveType?.toLowerCase().includes(search)
    );
  });

  const handleExportToExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Leave Balance Report");

    // Define columns
    worksheet.columns = [
      { header: "Employee Code", key: "EmployeeCode", width: 15 },
      { header: "First Name", key: "FirstName", width: 15 },
      { header: "Last Name", key: "LastName", width: 15 },
      { header: "Phone", key: "Phone", width: 15 },
      { header: "Created At", key: "CreatedAt", width: 20 },
      { header: "Position", key: "Position", width: 20 },
      { header: "Leave Year", key: "LeaveYear", width: 12 },
      { header: "Leave Type", key: "LeaveType", width: 12 },
      { header: "Leave Type Name", key: "LeaveTypeName", width: 20 },
      { header: "Accuration", key: "Accuration", width: 12 },
      { header: "Opening", key: "Opening", width: 10 },
      { header: "Reward", key: "Reward", width: 10 },
      { header: "Consumed", key: "Consumed", width: 10 },
      { header: "Balance", key: "Balance", width: 10 },
      { header: "Unique Code", key: "UniqueCode", width: 15 },
    ];

    // Style header row
    const headerRow = worksheet.getRow(1);
    headerRow.font = { bold: true, color: { argb: "FFFFFFFF" } };
    headerRow.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FF4472C4" },
    };
    headerRow.alignment = { horizontal: "center", vertical: "middle" };

    // Add data rows with colors
    filteredData.forEach((row) => {
      const dataRow = worksheet.addRow({
        EmployeeCode: row.EmployeeCode,
        FirstName: row.FirstName,
        LastName: row.LastName,
        Phone: row.Phone,
        CreatedAt: row.CreatedAt,
        Position: row.Position,
        LeaveYear: row.LeaveYear,
        LeaveType: row.LeaveType,
        LeaveTypeName: row.LeaveTypeName,
        Accuration: row.Accuration,
        Opening: row.Opening,
        Reward: row.Reward,
        Consumed: row.Consumed,
        Balance: row.Balance,
        UniqueCode: row.UniqueCode,
      });

      // Color balance cell based on value
      const balanceCell = dataRow.getCell("Balance");
      if (row.Balance > 0) {
        balanceCell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFC6EFCE" }, // Light green
        };
        balanceCell.font = { color: { argb: "FF006100" } };
      } else if (row.Balance === 0) {
        balanceCell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFFFEB9C" }, // Light yellow
        };
        balanceCell.font = { color: { argb: "FF9C5700" } };
      }

      // Color consumed cell if high
      const consumedCell = dataRow.getCell("Consumed");
      if (row.Consumed > 0) {
        consumedCell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFFFC7CE" }, // Light red
        };
        consumedCell.font = { color: { argb: "FF9C0006" } };
      }

      // Add borders
      dataRow.eachCell((cell) => {
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
      });
    });

    // Add borders to header
    headerRow.eachCell((cell) => {
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, `LeaveBalanceReport_${leaveYear}.xlsx`);
  };

  const renderRow = (row: FlattenedLeaveData, index: number) => (
    <tr key={`${row.UniqueCode}-${index}`}>
      <td>{row.EmployeeCode}</td>
      <td>{row.FirstName}</td>
      <td>{row.LastName}</td>
      <td>{row.Phone}</td>
      <td>{row.CreatedAt}</td>
      <td>{row.Position}</td>
      <td>{row.LeaveYear}</td>
      <td>
        <span
          style={{
            padding: "2px 8px",
            borderRadius: "4px",
            backgroundColor:
              row.LeaveType === "CL"
                ? "#E3F2FD"
                : row.LeaveType === "PL"
                  ? "#E8F5E9"
                  : row.LeaveType === "SL"
                    ? "#FFF3E0"
                    : "#F5F5F5",
            color:
              row.LeaveType === "CL"
                ? "#1565C0"
                : row.LeaveType === "PL"
                  ? "#2E7D32"
                  : row.LeaveType === "SL"
                    ? "#E65100"
                    : "#616161",
          }}
        >
          {row.LeaveType}
        </span>
      </td>
      <td>{row.LeaveTypeName || "-"}</td>
      <td>{row.Accuration}</td>
      <td>{row.Opening}</td>
      <td>{row.Reward}</td>
      <td>
        <span
          style={{
            color: row.Consumed > 0 ? "#D32F2F" : "inherit",
            fontWeight: row.Consumed > 0 ? "bold" : "normal",
          }}
        >
          {row.Consumed}
        </span>
      </td>
      <td>
        <span
          style={{
            color:
              row.Balance > 0
                ? "#2E7D32"
                : row.Balance === 0
                  ? "#FF8F00"
                  : "#D32F2F",
            fontWeight: "bold",
          }}
        >
          {row.Balance}
        </span>
      </td>
      <td>{row.UniqueCode}</td>
    </tr>
  );

  return (
    <div className="page-body p-2">
      <Row>
        <Col sm={12}>
          <Card>
            <div>
              <CardHeaderSpan
                headingClassName="pb-0"
                back={true}
                heading="Leave Balance Report"
                span={`Year: ${leaveYear}`}
              />
            </div>

            {/* Filters Row */}
            <div className="row px-3 py-1 mb-3 align-items-center justify-content-between">
              {/* Left side: Search and Position Filter */}
              <div className="col-md-8 d-flex align-items-center gap-3 flex-wrap">
                <Input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by code, name or phone"
                  className="form-control"
                  style={{ maxWidth: "250px" }}
                />
                <TreeModel onSelectionChange={setSelectedIds} />

                {/* Leave Type Filter */}
                <Dropdown
                  isOpen={leaveTypeDropdownOpen}
                  toggle={toggleLeaveTypeDropdown}
                >
                  <DropdownToggle caret color="primary" size="sm">
                    Leave Types
                    {selectedLeaveTypes.length > 0
                      ? ` (${selectedLeaveTypes.length})`
                      : ""}
                  </DropdownToggle>
                  <DropdownMenu className="p-2">
                    {leaveTypes.map((type) => (
                      <div
                        key={type.value}
                        className="form-check form-check-inline mb-0"
                      >
                        <Input
                          type="checkbox"
                          id={`leave-type-${type.value}`}
                          checked={selectedLeaveTypes.includes(type.value)}
                          onChange={() => handleLeaveTypeChange(type.value)}
                        />
                        <Label
                          className="form-check-label"
                          for={`leave-type-${type.value}`}
                        >
                          {type.label}
                        </Label>
                      </div>
                    ))}
                  </DropdownMenu>
                </Dropdown>
              </div>

              {/* Right side: Refresh and Export Buttons */}
              <div className="col-md-4 d-flex justify-content-end gap-3">
                <button
                  className="btn btn-sm btn-warning align-items-center d-flex"
                  onClick={handleApplyFilters}
                  disabled={loading}
                >
                  <FeatherIconCom
                    iconName="RefreshCw"
                    size={14}
                    className="text-light"
                  />
                  <span className="px-2" style={{ fontSize: 14 }}>
                    Refresh
                  </span>
                </button>
                <button
                  className="btn btn-sm btn-primary align-items-center d-flex"
                  onClick={handleExportToExcel}
                  disabled={filteredData.length === 0}
                >
                  <FeatherIconCom
                    iconName="Download"
                    size={14}
                    className="text-light"
                  />
                  <span className="px-2" style={{ fontSize: 14 }}>
                    Export
                  </span>
                </button>
              </div>
            </div>

            <CardBody className="pt-0">
              {loading ? (
                <Loader height="250px" />
              ) : (
                <CommonTableNew
                  tableClass="table-bordered"
                  hover
                  headClass="table"
                  headData={headData}
                  data={filteredData}
                  renderRow={renderRow}
                  initialRows={25}
                />
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default LeaveBalanceReport;
